import React, { useMemo, useState } from 'react';
import type { JobType } from '../graphql/types';
import * as jobTypeConfigService from '../services/jobTypeConfigService';
import type { PillTone } from '../components/ui';
import { TopBar } from './TopBar';
import { Rail } from './Rail';
import { CommentsPanel } from './comments/CommentsPanel';
import {
  anchorToWorkspaceTarget,
  resolveAnchor,
  type CommentAnchor,
  type CommentSelection,
} from './comments/commentsModel';
import { isCommentsEnabled } from './comments/commentsService';
import { useComments } from './comments/useComments';
import { FEATURE_HOME_PATH, getModeOptions, isModeLocked, type LockedModes } from './modes';
import type { WorkspaceMode, WorkspaceParamsUpdate } from './urlState';

/**
 * Workspace: the single full-bleed detail screen for one job, loan or search.
 * Top_Bar on top; below it the Rail, the active Mode's body (scrolls on its
 * own) and, when opened, the Comments_Panel overlay on the right.
 *
 * This container is presentational. Job loading, subscriptions and polling
 * (tasks 2.2, 2.3) and the locked-Mode rules (task 2.4) feed it through props,
 * and Mode bodies are passed as `children`.
 *
 * Requirements: 3.1, 3.2, 3.5, 3.6.
 */
export interface WorkspaceProps {
  feature: JobType;
  /** Item title. Falls back to the Feature name while the job is loading. */
  title?: string;
  mode: WorkspaceMode;
  onModeChange: (mode: WorkspaceMode) => void;
  /** Locked Modes mapped to the reason shown on hover. */
  locked?: LockedModes;
  statusLabel?: string;
  statusTone?: PillTone;
  /** Rail content; omit to hide the Rail for Modes that have none. */
  rail?: React.ReactNode;
  railLabel?: string;
  /** Id of the job, loan or search. Comments attach to it; drafts have none. */
  itemId?: string;
  /** Current URL selection; a new comment anchors to it (Requirement 12.3). */
  selection?: CommentSelection;
  /**
   * Apply Workspace URL params when the user selects a comment's Anchor
   * (Requirement 12.4). Without it, Anchor chips are plain labels.
   */
  onNavigateToAnchor?: (update: WorkspaceParamsUpdate) => void;
  /**
   * Show the Comments toggle and panel. Defaults to the
   * `VITE_COMMENTS_ENABLED` flag; the comments API does not exist yet.
   */
  commentsEnabled?: boolean;
  userName?: string;
  children?: React.ReactNode;
}

export const Workspace: React.FC<WorkspaceProps> = ({
  feature,
  title,
  mode,
  onModeChange,
  locked,
  statusLabel,
  statusTone,
  rail,
  railLabel = 'Workspace rail',
  itemId,
  selection,
  onNavigateToAnchor,
  commentsEnabled,
  userName,
  children,
}) => {
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [anchorNotice, setAnchorNotice] = useState<string | null>(null);
  const featureName = jobTypeConfigService.getDisplayName(feature);
  const itemTitle = title || featureName;

  const showComments = (commentsEnabled ?? isCommentsEnabled()) && !!itemId;
  const comments = useComments(showComments ? itemId : undefined);
  const anchor = useMemo(() => resolveAnchor(selection, mode), [selection, mode]);

  // A locked Mode can never be the active one (Requirement 3.5): if the URL or
  // a stale selection points at one, ignore requests to switch to it.
  const handleModeChange = (next: WorkspaceMode) => {
    if (isModeLocked(locked, next)) return;
    onModeChange(next);
  };

  // Selecting an Anchor navigates by URL params and leaves the panel open. A
  // locked or unavailable target is explained instead of opened.
  const handleSelectAnchor = (target: CommentAnchor) => {
    const result = anchorToWorkspaceTarget(target, feature, locked);
    if (!result.ok) {
      setAnchorNotice(result.message);
      return;
    }
    setAnchorNotice(null);
    onNavigateToAnchor?.(result.update);
  };

  return (
    <div className="flex h-screen min-w-[1180px] flex-col bg-[#f7f6fb] text-gray-900">
      <TopBar
        featureName={featureName}
        backTo={FEATURE_HOME_PATH[feature]}
        title={itemTitle}
        modes={getModeOptions(feature)}
        mode={mode}
        locked={locked}
        onModeChange={handleModeChange}
        statusLabel={statusLabel}
        statusTone={statusTone}
        commentsEnabled={showComments}
        commentCount={comments.count}
        commentsOpen={commentsOpen}
        onToggleComments={() => setCommentsOpen((open) => !open)}
        userName={userName}
      />
      <div className="relative flex min-h-0 flex-1">
        {rail ? <Rail label={railLabel}>{rail}</Rail> : null}
        <main data-testid="mode-body" className="min-w-0 flex-1 overflow-auto">
          {children}
        </main>
        {showComments && commentsOpen ? (
          <CommentsPanel
            itemName={itemTitle}
            entries={comments.entries}
            anchor={anchor}
            loadError={comments.loadError}
            onSelectAnchor={onNavigateToAnchor ? handleSelectAnchor : undefined}
            notice={anchorNotice}
            onClose={() => {
              setCommentsOpen(false);
              setAnchorNotice(null);
            }}
            onPost={(text) =>
              comments.post({ author: userName || 'You', text, anchor })
            }
          />
        ) : null}
      </div>
    </div>
  );
};

export default Workspace;
